import React, { useRef, useState } from 'react';
import { openExternalUrl } from '@/utils/url-utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  Download,
  ExternalLink,
  Info,
  Loader2,
  X,
} from 'lucide-react';
import { NodeFile, NodeCategory } from '@/types/node-types';
import { getNodeStyling } from '@/constants/node-styling';
import { getIconComponent } from '@/pages/workflow/helper/icon-mapping';
import { downloadFileWithName } from '@/services/workflow/get-file';
import {
  officeEmbedUrl,
  OFFICE_EXTENSIONS,
} from '@/pages/workflow/component/display-components';

interface FilesPreviewPanelProps {
  files: NodeFile[];
  nodeDisplayName: string;
  icon?: string;
  nodeType?: string;
  isVisible?: boolean;
  isLoading?: boolean;
  error?: string | null;
  onClose?: () => void;
}

export const FilesPreviewPanel: React.FC<FilesPreviewPanelProps> = ({
  files,
  nodeDisplayName,
  icon,
  nodeType,
  isVisible = false,
  isLoading = false,
  error = null,
  onClose,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  const nodeStyling = getNodeStyling(nodeType as NodeCategory);

  const handleDownload = async (file: NodeFile) => {
    const fileKey = file.id || file.s3_key;
    setDownloadingFiles((prev) => new Set(prev).add(fileKey));

    try {
      // Use download_url (with Content-Disposition) if available, fallback to presigned_url
      const downloadUrl = file.download_url || file.presigned_url;
      await downloadFileWithName(downloadUrl, file.name);
    } catch (err) {
      console.error('Failed to download file:', err);
    } finally {
      setDownloadingFiles((prev) => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
    }
  };

  const handlePreview = (file: NodeFile) => {
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (ext && OFFICE_EXTENSIONS.includes(ext)) {
      // Use short proxy_url for Office Online (URL length limit ~1500 chars)
      // Fall back to presigned_url if proxy not available
      const previewUrl = file.proxy_url || file.presigned_url;
      openExternalUrl(officeEmbedUrl(previewUrl));
    } else {
      openExternalUrl(file.presigned_url);
    }
  };

  const formatMimeType = (mimeType: string) => {
    const parts = mimeType.split('/');
    return parts.length > 1 ? parts[1].toUpperCase() : mimeType.toUpperCase();
  };

  const isFileDownloading = (file: NodeFile) => {
    const fileKey = file.id || file.s3_key;
    return downloadingFiles.has(fileKey);
  };

  const hasOfficeFiles =
    !isLoading &&
    !error &&
    files.some((f) =>
      OFFICE_EXTENSIONS.includes(f.name.split('.').pop()?.toLowerCase() ?? ''),
    );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className='w-full max-h-full bg-popover border border-border shadow-lg rounded-lg flex flex-col min-w-0 dark:bg-popover'
      onClick={(e) => e.stopPropagation()}
    >
      {/* Panel Header */}
      <div className='flex items-center justify-between p-4 border-b border-border'>
        <div className='flex items-center gap-2 text-sm font-semibold w-full'>
          <span style={{ color: nodeStyling.primaryColor }}>
            {getIconComponent(icon || '', nodeStyling.primaryColor)}
          </span>
          <span>{nodeDisplayName}</span>
          {!isLoading && !error && (
            <span className='text-muted-foreground font-normal'>
              ({files.length} file{files.length === 1 ? '' : 's'})
            </span>
          )}
          {isLoading && (
            <span className='text-muted-foreground font-normal flex items-center gap-1'>
              <Loader2 className='size-3 animate-spin' />
              Loading...
            </span>
          )}
        </div>
        <Button
          variant='ghost'
          size='sm'
          onClick={onClose}
          className='size-8 p-0'
        >
          <X className='size-4' />
        </Button>
      </div>

      {/* Panel Content */}
      <div className='flex-1 flex flex-col overflow-hidden min-h-0 max-h-full'>
        <div
          className='flex-1 p-4 bg-muted-background min-h-0 max-h-full overflow-auto'
          data-files-panel='true'
        >
          {/* Error State */}
          {error && (
            <div className='flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm'>
              <AlertCircle className='size-4 shrink-0' />
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {isLoading && !error && (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {/* Files Table */}
          {!isLoading && !error && files.length > 0 && (
            <Table tableRef={tableRef}>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className='text-right'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file, index) => (
                  <TableRow key={`${file.s3_key}-${index}`}>
                    <TableCell className='font-medium'>
                      <span
                        className='truncate max-w-[300px]'
                        title={file.name}
                      >
                        {file.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant='outline' className='text-xs'>
                        {formatMimeType(file.mime_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex items-center justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handlePreview(file)}
                          title='Open in new tab'
                          className='size-8 p-0'
                        >
                          <ExternalLink className='size-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handleDownload(file)}
                          title='Download file'
                          className='size-8 p-0'
                          disabled={isFileDownloading(file)}
                        >
                          {isFileDownloading(file) ? (
                            <Loader2 className='size-4 animate-spin' />
                          ) : (
                            <Download className='size-4' />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Empty State */}
          {!isLoading && !error && files.length === 0 && (
            <div className='flex items-center justify-center py-8 text-muted-foreground text-sm'>
              No files available
            </div>
          )}
        </div>
      </div>

      {/* Office Live disclaimer */}
      {hasOfficeFiles && (
        <div className='flex items-start gap-2 px-4 py-2.5 border-t border-border text-xs text-muted-foreground'>
          <Info className='size-3.5 shrink-0 mt-px' />
          <span>
            Preview uses Microsoft Office Online, which may occasionally be
            unavailable. Download the file to view it if there is an error.
          </span>
        </div>
      )}
    </div>
  );
};
